import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { MetadataMultiSelect } from "@/components/itsm-config/metadata-multi-select";
import { DeskSelect } from "@/components/itsm-config/desk-select";
import { TeamField, TeamLine } from "@/components/itsm-config/team-select";
import {
  ConditionsCell,
  ExportRuleConditionFields,
} from "@/components/itsm-config/export-rule-conditions";
import { ScopeSelect } from "@/components/itsm-config/scope-select";
import { MetadataSelect } from "@/components/itsm-config/metadata-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { useCatalogItemNames } from "@/hooks/use-catalog-item-names";
import { useItsmCredentials } from "@/hooks/use-itsm-config";
import { useDraft } from "@/components/itsm-config/draft-context";
import { useJiraIssueTypes } from "@/hooks/use-itsm-metadata";
import {
  useCredentialName,
  useDeskName,
  useScopeLabel,
} from "@/hooks/use-itsm-labels";
import {
  EMPTY_CONDITIONS,
  conditionFormShape,
  conditionsMatch,
  conditionsToForm,
  hasConditionValues,
  type ConditionValues,
} from "@/lib/itsm-config/export-rule-conditions";
import { teamMatch } from "@/lib/itsm-config/export-rule-team";
import {
  EXCLUDABLE_FIELDS,
  PROVIDER_LABELS,
  type ExportMappingRule,
  type ProviderConfig,
  type ItsmProviderFamily,
} from "@/lib/types/itsm-config";

/** Row cells that resolve ids → names (hooks can't run inside .map()). */
function DeskCell({ tenantId, deskId }: { tenantId: string; deskId?: string | null }) {
  const name = useDeskName(tenantId, deskId);
  return <>{name || "— any —"}</>;
}

function CredentialCell({
  tenantId,
  credentialId,
}: {
  tenantId: string;
  credentialId?: string | null;
}) {
  const name = useCredentialName(tenantId, credentialId);
  return <>{name || "— all —"}</>;
}

function ScopeCell({
  family,
  tenantId,
  credentialId,
  scope,
}: {
  family: ItsmProviderFamily;
  tenantId: string;
  credentialId: string;
  scope?: string | null;
}) {
  const label = useScopeLabel(family, tenantId, credentialId, scope);
  return <>{label || "—"}</>;
}

const SCOPE_LABELS: Record<string, string> = {
  jira: "Project key",
  servicenow: "Assignment group",
  freshservice: "Workspace",
};

const SCOPE_DESCRIPTIONS: Record<string, string> = {
  jira: "The Jira project tickets on the matched desk are created in.",
  servicenow:
    "Sets the default assignment group for tickets this rule creates in " +
    "ServiceNow — new tickets from the matched desk are routed to this group.",
  freshservice:
    "The Freshservice workspace tickets on the matched desk are created in.",
};

const formSchema = z
  .object({
    desk_id: z.string().trim().optional(),
    subdesk_id: z.string().trim().optional(),
    integration_id: z.string().trim().optional(),
    match_issue_type: z.string().trim().optional(),
    provider_scope: z.string().trim().optional(),
    issue_type: z.string().trim().optional(),
    excluded_fields: z.array(z.string()).optional(),
    ...conditionFormShape,
  })
  .refine(hasConditionValues, {
    path: ["custom_field_values"],
    message: "Add at least one value",
  })
  .refine(
    // The backend refuses a "Don't sync" catch-all: it would win the tie
    // against the tenant's own catch-all and silently stop every export.
    (v) =>
      !v.exclude ||
      Boolean(
        v.desk_id ||
          v.match_issue_type ||
          v.service_catalog_item_ids.length ||
          v.custom_field_name,
      ),
    {
      path: ["exclude"],
      message: "Pick a desk, issue type, catalog item or custom field",
    },
  );
type FormValues = z.infer<typeof formSchema>;

const EMPTY_FORM: FormValues = { excluded_fields: [], ...EMPTY_CONDITIONS };

// Families whose create path is verified to honor catalog-item and custom
// field conditions and "Don't sync". The others keep any stored conditions
// on save but don't offer them.
const CONDITION_FAMILIES = new Set<ItsmProviderFamily>(["jira"]);

interface Props {
  tenantId: string;
  credentialId: string;
  provider: string;
  family: ItsmProviderFamily;
  providerConfig: ProviderConfig;
}

export function ExportRulesSection({
  tenantId,
  credentialId,
  provider,
  family,
  providerConfig,
}: Props) {
  // eslint-disable-next-line security/detect-object-injection -- provider is a fixed union
  const providerLabel = PROVIDER_LABELS[provider] ?? provider;
  const rules = providerConfig.export_mapping ?? [];
  // -1 = add; >=0 = edit that index; null = closed.
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const { update } = useDraft();

  // eslint-disable-next-line security/detect-object-injection -- family is a fixed provider-family union
  const scopeLabel = SCOPE_LABELS[family] ?? "Scope";
  // eslint-disable-next-line security/detect-object-injection -- family is a fixed provider-family union
  const scopeDescription = SCOPE_DESCRIPTIONS[family];

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: EMPTY_FORM,
  });
  const supportsConditions = CONDITION_FAMILIES.has(family);
  const catalog = useCatalogItemNames(tenantId);
  // Families without the conditions keep a stored exclude but show the
  // destination fields, which they cannot switch off here.
  const excluded = supportsConditions && form.watch("exclude");
  const selectedDesk = form.watch("desk_id");
  // A stored condition stays visible on every family, so an exclusion
  // narrowed to a catalog item does not read as the whole desk.
  const showConditions =
    supportsConditions ||
    rules.some(
      (r) => r.match.service_catalog_item_ids?.length || r.match.custom_field,
    );

  const { data: credentials } = useItsmCredentials(tenantId);
  const credentialOptions = (credentials ?? [])
    .filter((c) => c.provider === provider)
    .map((c) => ({ value: c.id, label: `${c.name} (${c.provider})` }));

  // Jira issue types cascade off the selected project (provider_scope).
  const selectedScope = form.watch("provider_scope");
  const issueTypes = useJiraIssueTypes(
    tenantId,
    credentialId,
    family === "jira" ? selectedScope || undefined : undefined,
  );

  const ruleToForm = (rule: ExportMappingRule): FormValues => ({
    desk_id: rule.match.desk_id ?? "",
    subdesk_id: rule.match.subdesk_id ?? "",
    integration_id: rule.match.integration_id ?? "",
    match_issue_type: rule.match.issue_type ?? "",
    provider_scope: rule.provider_scope ?? "",
    issue_type: rule.issue_type ?? "",
    excluded_fields: rule.excluded_fields ?? [],
    ...conditionsToForm(rule),
  });

  const openAdd = () => {
    form.reset(EMPTY_FORM);
    setEditIndex(-1);
  };
  const openEdit = (i: number) => {
    // eslint-disable-next-line security/detect-object-injection -- i is a numeric array index from a rendered row
    form.reset(ruleToForm(rules[i]));
    setEditIndex(i);
  };

  // Writes the rule list into the page draft; persisted on the page Save.
  const persist = (nextRules: ExportMappingRule[]) => {
    update({ export_mapping: nextRules.length ? nextRules : null });
  };

  const onSubmit = (values: FormValues) => {
    // Spread the rule being edited so fields this form doesn't own survive
    // (create_on_statuses, close_on_desk_change, and conditions on families
    // that don't offer them).
    const original =
      // eslint-disable-next-line security/detect-object-injection -- editIndex is a validated array index
      editIndex !== null && editIndex >= 0 ? rules[editIndex] : undefined;
    const exclude = supportsConditions
      ? values.exclude
      : Boolean(original?.exclude);
    const rule: ExportMappingRule = {
      ...original,
      match: {
        ...original?.match,
        desk_id: values.desk_id || null,
        subdesk_id: teamMatch(values.desk_id, values.subdesk_id),
        // A rule bound to a credential outranks any unbound rule, so an
        // exclusion left unbound could never beat a bound include. Pin it
        // to the credential being edited unless the author chose one.
        integration_id:
          values.integration_id ||
          (supportsConditions && exclude ? credentialId : null) ||
          null,
        issue_type: values.match_issue_type || null,
        ...(supportsConditions ? conditionsMatch(values) : {}),
      },
      // An excluded ticket is created nowhere, so a destination would only
      // mislead.
      provider_scope: exclude ? null : values.provider_scope || null,
      issue_type: exclude ? null : values.issue_type || null,
      excluded_fields:
        !exclude && values.excluded_fields?.length
          ? values.excluded_fields
          : null,
      exclude: exclude || null,
    };
    const next =
      editIndex === -1
        ? [...rules, rule]
        : rules.map((r, i) => (i === editIndex ? rule : r));
    persist(next);
    setEditIndex(null);
    form.reset(EMPTY_FORM);
  };

  const onDelete = () => {
    if (deleteIndex === null) return;
    const next = rules.filter((_, i) => i !== deleteIndex);
    persist(next);
    setDeleteIndex(null);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle>Where tickets go</CardTitle>
          <CardDescription>
            When Harmony creates a ticket in {providerLabel}, send this desk's
            tickets to a {scopeLabel.toLowerCase()}.
            {supportsConditions &&
              " Narrow a rule to a team, catalog items or a custom field value, or " +
                "mark it \"Don't sync\" to keep matching tickets out; the " +
                "most specific rule wins."}
          </CardDescription>
        </div>
        <Button size="sm" onClick={openAdd}>
          <Plus className="mr-2 h-4 w-4" />
          Add rule
        </Button>
      </CardHeader>
      <CardContent>
        {rules.length === 0 ? (
          <p className="text-muted-foreground py-6 text-center text-sm">
            No export rules. Add one to route a desk to a {scopeLabel.toLowerCase()}.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Desk</TableHead>
                {showConditions && <TableHead>Conditions</TableHead>}
                <TableHead>{scopeLabel}</TableHead>
                <TableHead>Issue type</TableHead>
                <TableHead>Excluded fields</TableHead>
                <TableHead>Credential</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((rule, i) => (
                <TableRow
                  // eslint-disable-next-line react-x/no-array-index-key -- config rules have no stable id
                  key={`${rule.match.desk_id ?? ""}-${rule.provider_scope ?? ""}-${i}`}
                  className="cursor-pointer"
                  onClick={() => { openEdit(i); }}
                >
                  <TableCell>
                    <DeskCell tenantId={tenantId} deskId={rule.match.desk_id} />
                    <TeamLine
                      tenantId={tenantId}
                      deskId={rule.match.desk_id}
                      subdeskId={rule.match.subdesk_id}
                    />
                  </TableCell>
                  {showConditions && (
                    <TableCell>
                      <ConditionsCell rule={rule} catalogNames={catalog.names} />
                    </TableCell>
                  )}
                  {rule.exclude ? (
                    <TableCell colSpan={3}>
                      <span className="text-destructive font-medium">
                        Don&apos;t sync
                      </span>
                    </TableCell>
                  ) : (
                    <>
                      <TableCell>
                        <ScopeCell
                          family={family}
                          tenantId={tenantId}
                          credentialId={credentialId}
                          scope={rule.provider_scope}
                        />
                      </TableCell>
                      <TableCell>{rule.issue_type ?? "—"}</TableCell>
                      <TableCell>
                        {rule.excluded_fields?.length
                          ? rule.excluded_fields.join(", ")
                          : "—"}
                      </TableCell>
                    </>
                  )}
                  <TableCell className="text-muted-foreground text-xs">
                    <CredentialCell
                      tenantId={tenantId}
                      credentialId={rule.match.integration_id}
                    />
                  </TableCell>
                  <TableCell onClick={(e) => { e.stopPropagation(); }}>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => { setDeleteIndex(i); }}
                    >
                      <Trash2 className="text-destructive h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Add / edit dialog */}
      <Dialog
        open={editIndex !== null}
        onOpenChange={(o) => { if (!o) setEditIndex(null); }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editIndex === -1 ? "Add export rule" : "Edit export rule"}
            </DialogTitle>
            <DialogDescription>
              A ticket on the matched desk is created in the given {scopeLabel.toLowerCase()}.
              Leave a match field blank to match anything.
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={(e) => { void form.handleSubmit(onSubmit)(e); }}
              className="space-y-4"
              id="export-rule-form"
            >
              <FormField
                control={form.control}
                name="desk_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Harmony desk</FormLabel>
                    <FormControl>
                      <DeskSelect
                        tenantId={tenantId}
                        value={field.value ?? ""}
                        onChange={(v) => {
                          field.onChange(v);
                          // Teams belong to the desk they were picked from.
                          if (v !== field.value) form.setValue("subdesk_id", "");
                        }}
                        placeholder="Select a desk (blank = all desks)"
                      />
                    </FormControl>
                    <FormDescription>
                      Which desk this rule routes. Leave unset to match all desks.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <TeamField
                form={form as unknown as UseFormReturn<{ subdesk_id?: string }>}
                tenantId={tenantId}
                deskId={selectedDesk || undefined}
              />
              {supportsConditions && (
                <ExportRuleConditionFields
                  form={form as unknown as UseFormReturn<ConditionValues>}
                  tenantId={tenantId}
                  deskId={selectedDesk || undefined}
                  excludeDescription={
                    `Tickets this rule matches are not created in ${providerLabel}. ` +
                    "It wins over rules that are equally or less specific; a " +
                    `more specific rule that names a ${scopeLabel.toLowerCase()} ` +
                    "still wins."
                  }
                />
              )}
              {!excluded && (
                <FormField
                  control={form.control}
                  name="provider_scope"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{scopeLabel}</FormLabel>
                      <FormControl>
                        <ScopeSelect
                          family={family}
                          tenantId={tenantId}
                          integrationId={credentialId}
                          value={field.value ?? ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      {scopeDescription && (
                        <FormDescription>{scopeDescription}</FormDescription>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              {family === "jira" && !excluded && (
                <FormField
                  control={form.control}
                  name="issue_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Issue type</FormLabel>
                      <FormControl>
                        <MetadataSelect
                          options={issueTypes.data?.options ?? []}
                          isLoading={issueTypes.isLoading}
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          placeholder="e.g. Task, Incident"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              {family === "jira" && !excluded && (
                <FormField
                  control={form.control}
                  name="excluded_fields"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Excluded fields</FormLabel>
                      <FormControl>
                        <MetadataMultiSelect
                          options={EXCLUDABLE_FIELDS}
                          value={field.value ?? []}
                          onChange={field.onChange}
                          allowCustom={false}
                          placeholder="Select fields to skip..."
                        />
                      </FormControl>
                      <FormDescription>
                        Harmony fields to skip when creating the ticket.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name="integration_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Credential id (optional)</FormLabel>
                    <FormControl>
                      <MetadataSelect
                        options={credentialOptions}
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        placeholder="All credentials"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditIndex(null); }}>
              Cancel
            </Button>
            <Button type="submit" form="export-rule-form">
              {editIndex === -1 ? "Add rule" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog
        open={deleteIndex !== null}
        onOpenChange={(o) => { if (!o) setDeleteIndex(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete export rule?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the rule from the provider config. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
